/**
 * AB#21242 — "45 minutes ago" as a table column.
 *
 * The Days Since Action column is a duration you scan down, comparing rows, not
 * a sentence you read. "45 minutes ago" against "3 hours ago" against "a day
 * ago" costs the column 150px and makes the comparison harder, because the eye
 * has to parse three different shapes to answer "which of these is oldest".
 *
 * A fixed `<number><unit>` shape lines up, sorts visually, and fits in a column
 * narrow enough to keep on screen. The exact value stays available as a tooltip
 * — this is a compaction of the display, not of the data.
 *
 * Deliberately a separate function rather than a change to `timeAgo`, which
 * reads correctly in prose everywhere else it is used.
 */

const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const MONTH = 30 * DAY;
const YEAR = 365 * DAY;

/**
 * A duration in at most four characters: `12m`, `3h`, `6d`, `2mo`, `1y`.
 *
 * Rounds DOWN throughout, so a value never claims more elapsed time than has
 * actually passed — "2h" on something 1h59m old is a small lie in the direction
 * that matters on an ageing queue.
 */
export const compactDuration = (
  dateString: string | null | undefined,
  now: Date = new Date()
): string => {
  if (!dateString) return '—';

  const then = new Date(dateString);
  if (Number.isNaN(then.getTime())) return '—';

  const seconds = Math.floor((now.getTime() - then.getTime()) / 1000);

  // A clock skew of a few seconds between server and browser is normal and must
  // not render as "-1m".
  if (seconds < 0) return 'now';
  if (seconds < MINUTE) return 'now';
  if (seconds < HOUR) return `${Math.floor(seconds / MINUTE)}m`;
  if (seconds < DAY) return `${Math.floor(seconds / HOUR)}h`;
  if (seconds < MONTH) return `${Math.floor(seconds / DAY)}d`;
  if (seconds < YEAR) return `${Math.floor(seconds / MONTH)}mo`;
  return `${Math.floor(seconds / YEAR)}y`;
};
